import { useParams } from "react-router-dom";
import { useStore } from "../../../../../helpers";
import { useUploadSource, useUpdateSource } from "../../../tars-hooks";
import type { UploadSourcePayload } from "../../../tars-hooks/useUploadSource";
import type { SourceNode } from "../sources-types";
import { buildUpdateSourcePayload, parseNotebookId, toParentIdNum } from "./addSourceUtils";

type SubmitPayload = Omit<UploadSourcePayload, "notebook_id" | "parent_id" | "onUploadSuccess">;

export function useSourceForm(
  parentId: string | null,
  editNode: SourceNode | null,
  onSuccess: () => void
) {
  const { notebookId: notebookIdStr } = useParams<{ notebookId: string }>();
  const { tarsStore } = useStore();
  const { mutateAsync: uploadSource, isLoading: isUploading } = useUploadSource();
  const { mutateAsync: updateSource } = useUpdateSource();
  const isEdit = editNode != null;
  const notebookIdNum = parseNotebookId(notebookIdStr);

  const submitSource = async (payload: SubmitPayload) => {
    if (notebookIdNum == null) return;
    try {
      const created = await uploadSource({
        notebook_id: notebookIdNum,
        parent_id: toParentIdNum(parentId),
        ...payload,
      });
      // Pass localParentId so the item is placed under the correct parent
      // regardless of what parent_id the server returns in the response.
      tarsStore.addSourceFromServer(created, parentId, payload.more);
      onSuccess();
    } catch {
      // Error handled by useUploadSource
    }
  };

  const renameNode = async (name: string) => {
    if (!editNode) return;
    const sourceId = Number(editNode.id);
    if (notebookIdNum == null || Number.isNaN(sourceId)) {
      tarsStore.rename(editNode.id, name);
      onSuccess();
      return;
    }
    try {
      await updateSource(buildUpdateSourcePayload(editNode, notebookIdNum, sourceId, name));
      tarsStore.rename(editNode.id, name);
      onSuccess();
    } catch {
      // Error toast from useUpdateSource
    }
  };

  const updateNodeFields = async (updates: { name?: string; url?: string }) => {
    if (!editNode) return;
    const sourceId = Number(editNode.id);
    const morePatch: Record<string, unknown> = {};
    if (updates.url !== undefined) morePatch.url = updates.url;
    if (notebookIdNum == null || Number.isNaN(sourceId)) {
      tarsStore.updateNode(editNode.id, updates);
      onSuccess();
      return;
    }
    const name = updates.name ?? editNode.name;
    try {
      await updateSource(
        buildUpdateSourcePayload(editNode, notebookIdNum, sourceId, name, morePatch)
      );
      tarsStore.updateNode(editNode.id, updates);
      onSuccess();
    } catch {
      // Error toast from useUpdateSource
    }
  };

  return {
    isEdit,
    notebookIdNum,
    tarsStore,
    uploadSource,
    isUploading,
    submitSource,
    renameNode,
    updateNodeFields,
  };
}
